import { create } from 'zustand';
import { apiService } from '../services/apiService';
import { BRANDING_CONFIG } from '../config/branding';
import { useAuthStore } from './authStore';
import { ensureLogoCacheUpToDate, clearCachedLogo } from '../utils/logoCache';
import { safeLocalStorageSetItem, safeLocalStorageSetJson } from '../utils/safeLocalStorage';

// Тип данных брендинга согласно API
export interface BrandingData {
  id?: number;
  companyName: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  welcomeMessage?: string;
  benefits?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Тип данных компании
export interface CompanyData {
  id: number;
  name: string;
  description?: string;
  status: string;
  ownerId?: number;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
  relevanterEnabled?: boolean | null;
  atsEnabled?: boolean | null;
}

interface BrandingState {
  // Состояние
  loaded: boolean;
  data: BrandingData | null;
  companyData: CompanyData | null;
  isLoading: boolean;
  error: string | null;
  
  // Действия
  setBranding: (data: BrandingData) => void;
  clearBranding: () => void;
  initFromStorage: () => void;
  loadBranding: () => Promise<void>;
  loadCompanyData: () => Promise<void>;
  getCompanyName: () => string;
  getDefaultCompanyName: () => string;
  // Цвета брендинга
  getPrimaryColor: () => string;
  getPrimaryHoverColor: () => string;
  getAiColor: () => string;
  getAiHoverColor: () => string;
  // Принудительное обновление CSS переменных
  forceUpdateCSSVariables: () => void;
}

// Ключ для localStorage
const BRANDING_STORAGE_KEY = 'branding:v1';
const BRANDING_COLOR_KEY = 'branding:color';

// Функция для получения сохраненного цвета
const getStoredBrandingColor = (): string | null => {
  try {
    return localStorage.getItem(BRANDING_COLOR_KEY);
  } catch (error) {
    console.warn('🔍 Failed to get branding color from localStorage:', error);
    return null;
  }
};

// Функция для сохранения цвета
const saveBrandingColor = (color: string): void => {
  try {
    safeLocalStorageSetItem(BRANDING_COLOR_KEY, color);
  } catch (error) {
    console.warn('🔍 Failed to save branding color to localStorage:', error);
  }
};

// Функция для проверки изменения цвета
const hasColorChanged = (currentColor: string): boolean => {
  const storedColor = getStoredBrandingColor();
  return storedColor !== currentColor;
};

// Функция для затемнения цвета
const darkenColor = (color: string, percent: number): string => {
  // Убираем # если есть
  const hex = color.replace('#', '');
  
  // Конвертируем в RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Затемняем
  const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
  
  // Конвертируем обратно в hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Сохранение в localStorage
const saveBrandingToStorage = (data: BrandingData) => {
  try {
    safeLocalStorageSetJson(BRANDING_STORAGE_KEY, data);
    console.log('🔍 Branding saved to localStorage:', data.companyName);
  } catch (error) {
    console.warn('🔍 Failed to save branding to localStorage:', error);
  }
};

// Загрузка из localStorage
const loadBrandingFromStorage = (): BrandingData | null => {
  try {
    const stored = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as BrandingData;
      console.log('🔍 Branding loaded from localStorage:', data.companyName);
      return data;
    }
  } catch (error) {
    console.warn('🔍 Failed to load branding from localStorage:', error);
  }
  return null;
};

// Очистка localStorage
const clearBrandingFromStorage = () => {
  try {
    localStorage.removeItem(BRANDING_STORAGE_KEY);
    console.log('🔍 Branding cleared from localStorage');
  } catch (error) {
    console.warn('🔍 Failed to clear branding from localStorage:', error);
  }
};

/**
 * Стор для управления брендингом компании
 * 
 * Функциональность:
 * - Загрузка брендинга через API
 * - Сохранение/загрузка из localStorage
 * - Предоставление названия компании для UI
 * - Обработка ошибок и фолбэков
 */
export const useBrandingStore = create<BrandingState>((set, get) => ({
  // Начальное состояние
  loaded: false,
  data: null,
  companyData: null,
  isLoading: false,
  error: null,

  // Установка данных брендинга
  setBranding: (data: BrandingData) => {
    const currentColor = data.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
    
    // Проверяем, изменился ли цвет брендинга
    const colorChanged = hasColorChanged(currentColor);
    
    if (colorChanged) {
      console.log('🔍 Branding color changed, clearing cache and updating CSS variables');
      
      // Очищаем кеш браузера для CSS переменных
      const root = document.documentElement;
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-primary-hover');
      
      // Принудительно обновляем все элементы с брендинговыми цветами
      const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
      elements.forEach(el => {
        if (el instanceof HTMLElement) {
          // Сбрасываем стили для принудительного обновления
          const currentStyle = el.style.cssText;
          el.style.cssText = '';
          setTimeout(() => {
            el.style.cssText = currentStyle;
          }, 10);
        }
      });
    }
    
    set({ data, loaded: true, error: null });
    saveBrandingToStorage(data);
    saveBrandingColor(currentColor);
    
    // Обновляем CSS-переменные сразу после установки данных
    try {
      const primary = data.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
      const primaryHover = darkenColor(primary, 12);
      const root = document.documentElement;
      
      // Принудительно обновляем CSS переменные
      root.style.setProperty('--brand-primary', primary);
      root.style.setProperty('--brand-primary-hover', primaryHover);
      
      // Дополнительное обновление через небольшую задержку для борьбы с кешированием
      setTimeout(() => {
        root.style.setProperty('--brand-primary', primary);
        root.style.setProperty('--brand-primary-hover', primaryHover);
        
        // Принудительно обновляем все элементы с брендинговыми цветами
        const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            const currentStyle = el.style.cssText;
            el.style.cssText = currentStyle; // Принудительное обновление
          }
        });
      }, 100);
      
      console.log('🔍 CSS variables updated in setBranding:', { primary, primaryHover, colorChanged: colorChanged });
    } catch (error) {
      console.warn('🔍 Failed to update CSS variables in setBranding:', error);
    }
  },

  // Очистка данных брендинга
  clearBranding: () => {
    set({ data: null, companyData: null, loaded: false, error: null });
    clearBrandingFromStorage();
    // Очищаем кэш логотипа при логауте/сбросе
    clearCachedLogo();
    
    // Очищаем сохраненный цвет брендинга
    try {
      localStorage.removeItem(BRANDING_COLOR_KEY);
    } catch (error) {
      console.warn('🔍 Failed to clear branding color:', error);
    }
    
    // Сбрасываем CSS-переменные на дефолтные значения
    try {
      const primary = BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
      const primaryHover = darkenColor(primary, 12);
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', primary);
      root.style.setProperty('--brand-primary-hover', primaryHover);
      console.log('🔍 CSS variables reset to default in clearBranding:', { primary, primaryHover });
    } catch (error) {
      console.warn('🔍 Failed to reset CSS variables in clearBranding:', error);
    }
  },

  // Инициализация из localStorage
  initFromStorage: () => {
    const storedData = loadBrandingFromStorage();
    if (storedData) {
      const currentColor = storedData.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
      
      // Проверяем, изменился ли цвет брендинга
      const colorChanged = hasColorChanged(currentColor);
      
      if (colorChanged) {
        console.log('🔍 Branding color changed on init, clearing cache');
        
        // Очищаем кеш браузера для CSS переменных
        const root = document.documentElement;
        root.style.removeProperty('--brand-primary');
        root.style.removeProperty('--brand-primary-hover');
        
        // Принудительно обновляем все элементы с брендинговыми цветами
        const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            // Сбрасываем стили для принудительного обновления
            const currentStyle = el.style.cssText;
            el.style.cssText = '';
            setTimeout(() => {
              el.style.cssText = currentStyle;
            }, 10);
          }
        });
        
        // Обновляем сохраненный цвет
        saveBrandingColor(currentColor);
      }
      
      set({ data: storedData, loaded: true, error: null });
      console.log('🔍 Branding initialized from storage');
      
      // Обновляем CSS-переменные после инициализации из localStorage
      try {
        const primary = storedData.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
        const primaryHover = darkenColor(primary, 12);
        const root = document.documentElement;
        
        // Принудительно обновляем CSS переменные
        root.style.setProperty('--brand-primary', primary);
        root.style.setProperty('--brand-primary-hover', primaryHover);
        
        // Дополнительное обновление через небольшую задержку для борьбы с кешированием
        setTimeout(() => {
          root.style.setProperty('--brand-primary', primary);
          root.style.setProperty('--brand-primary-hover', primaryHover);
          
          // Принудительно обновляем все элементы с брендинговыми цветами
          const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
          elements.forEach(el => {
            if (el instanceof HTMLElement) {
              const currentStyle = el.style.cssText;
              el.style.cssText = currentStyle; // Принудительное обновление
            }
          });
          
          console.log('🔍 CSS variables force updated in initFromStorage:', { primary, primaryHover, colorChanged: colorChanged });
        }, 100);
        
        console.log('🔍 CSS variables updated in initFromStorage:', { primary, primaryHover });
      } catch (error) {
        console.warn('🔍 Failed to update CSS variables in initFromStorage:', error);
      }
    }
  },

  // Загрузка брендинга через API
  loadBranding: async () => {
    const { isLoading } = get();
    if (isLoading) {
      console.log('🔍 Branding already loading, skipping');
      return;
    }

    // Брендинг доступен для всех авторизованных пользователей
    const { role } = useAuthStore.getState();
    console.log('🔍 BrandingStore: loadBranding вызван, роль пользователя:', role);

    set({ isLoading: true, error: null });
    
    try {
      console.log('🔍 Loading branding from API...');
      const response = await apiService.getAuthenticatedClient().branding.getMyCompanyBranding();
      const brandingData = response.data as BrandingData;
      
      console.log('🔍 Branding loaded from API:', brandingData.companyName);
      
      // Проверяем цвет брендинга при каждом запросе к API
      const currentColor = brandingData.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
      const colorChanged = hasColorChanged(currentColor);
      
      if (colorChanged) {
        console.log('🔍 Branding color changed on API load, clearing cache and updating CSS variables');
        
        // Очищаем кеш браузера для CSS переменных
        const root = document.documentElement;
        root.style.removeProperty('--brand-primary');
        root.style.removeProperty('--brand-primary-hover');
        
        // Принудительно обновляем все элементы с брендинговыми цветами
        const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            // Сбрасываем стили для принудительного обновления
            const currentStyle = el.style.cssText;
            el.style.cssText = '';
            setTimeout(() => {
              el.style.cssText = currentStyle;
            }, 10);
          }
        });
      }
      
      set({ 
        data: brandingData, 
        loaded: true, 
        isLoading: false, 
        error: null 
      });
      
      // Сохраняем в localStorage и обновляем цвет
      saveBrandingToStorage(brandingData);
      saveBrandingColor(currentColor);

      // Обновляем CSS переменные после загрузки из API
      try {
        const primary = brandingData.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
        const primaryHover = darkenColor(primary, 12);
        const root = document.documentElement;
        
        // Принудительно обновляем CSS переменные
        root.style.setProperty('--brand-primary', primary);
        root.style.setProperty('--brand-primary-hover', primaryHover);
        
        // Дополнительное обновление через небольшую задержку для борьбы с кешированием
        setTimeout(() => {
          root.style.setProperty('--brand-primary', primary);
          root.style.setProperty('--brand-primary-hover', primaryHover);
          
          // Принудительно обновляем все элементы с брендинговыми цветами
          const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
          elements.forEach(el => {
            if (el instanceof HTMLElement) {
              const currentStyle = el.style.cssText;
              el.style.cssText = currentStyle; // Принудительное обновление
            }
          });
        }, 100);
        
        console.log('🔍 CSS variables updated after API load:', { primary, primaryHover, colorChanged: colorChanged });
      } catch (error) {
        console.warn('🔍 Failed to update CSS variables after API load:', error);
      }

      // Обновляем кэш логотипа в фоне
      if (brandingData.logoUrl) {
        try {
          const { resolveUrl } = await import('../utils/urlUtils');
          const abs = resolveUrl(brandingData.logoUrl);
          ensureLogoCacheUpToDate(abs);
        } catch {}
      }
      
    } catch (error) {
      console.warn('🔍 Failed to load branding from API:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Ошибка загрузки брендинга' 
      });
      
      // Не блокируем работу приложения при ошибке брендинга
    }
  },

  // Загрузка данных компании для получения названия по умолчанию
  loadCompanyData: async () => {
    try {
      console.log('🔍 Loading company data from API...');
      const companyData = await apiService.getMyCompany();
      
      console.log('🔍 Company data loaded from API:', companyData.name);
      set({ companyData });
      
    } catch (error) {
      console.warn('🔍 Failed to load company data from API:', error);
      // Не блокируем работу приложения при ошибке загрузки компании
    }
  },

  // Получение названия компании с фолбэком
  getCompanyName: () => {
    const { data } = get();
    return data?.companyName || BRANDING_CONFIG.APP_NAME;
  },

  // Получение названия компании по умолчанию (из данных компании)
  getDefaultCompanyName: () => {
    const { companyData } = get();
    return companyData?.name || BRANDING_CONFIG.APP_NAME;
  },

  // Цвета брендинга с фолбэками
  getPrimaryColor: () => {
    const { data } = get();
    return (data?.primaryColor && data.primaryColor.trim()) || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
  },
  getPrimaryHoverColor: () => {
    // Всегда вычисляем затемнение primaryColor на 12%
    const primaryColor = get().getPrimaryColor();
    return darkenColor(primaryColor, 12);
  },
  getAiColor: () => {
    // Используем secondaryColor брендинга как цвет для AI/"Сгенерировать" кнопок
    const { data } = get();
    return (data?.secondaryColor && data.secondaryColor.trim()) || BRANDING_CONFIG.DEFAULT_AI_COLOR;
  },
  getAiHoverColor: () => {
    // Всегда вычисляем затемнение aiColor на 12%
    const aiColor = get().getAiColor();
    return darkenColor(aiColor, 12);
  },

  // Принудительное обновление CSS переменных
  forceUpdateCSSVariables: () => {
    const { data } = get();
    if (data) {
      const primary = data.primaryColor || BRANDING_CONFIG.DEFAULT_PRIMARY_COLOR;
      const primaryHover = darkenColor(primary, 12);
      const root = document.documentElement;
      
      // Принудительно обновляем CSS переменные
      root.style.setProperty('--brand-primary', primary);
      root.style.setProperty('--brand-primary-hover', primaryHover);
      
      // Принудительно обновляем все элементы с брендинговыми цветами
      const elements = document.querySelectorAll('[style*="--brand-primary"], [style*="color: var(--brand-primary)"]');
      elements.forEach(el => {
        if (el instanceof HTMLElement) {
          const currentStyle = el.style.cssText;
          el.style.cssText = currentStyle; // Принудительное обновление
        }
      });
      
      console.log('🔍 CSS variables force updated:', { primary, primaryHover });
    }
  },
}));
